import React, {
  useEffect,
  useRef,
  useState,
  Children,
  isValidElement,
  cloneElement,
} from 'react';

/**
 * Reveal — wraps content with an IntersectionObserver-driven fade-in-up
 * animation. Adds `animate-fade-in-up` (0.5s ease-out forwards) when the
 * element enters the viewport. Starts at `opacity-0` to prevent flash.
 *
 * Props:
 *   as          — element/tag to render (default 'div')
 *   stagger     — when true, applies the animation to each direct child with
 *                 an incremental `animationDelay` of `childDelay` seconds.
 *   baseDelay   — initial animation-delay (seconds) on the first child / self
 *   childDelay  — per-child increment in stagger mode (default 0.1s)
 *
 * Animation is one-shot; element stays at final state after first reveal.
 */
const Reveal = ({
  as: Tag = 'div',
  stagger = false,
  baseDelay = 0,
  childDelay = 0.1,
  className = '',
  style,
  children,
  ...rest
}) => {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed) return;
    const node = ref.current;
    if (!node) return;
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      setRevealed(true);
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [revealed]);

  if (stagger) {
    const arr = Children.toArray(children);
    return (
      <Tag ref={ref} className={className} style={style} {...rest}>
        {arr.map((child, i) => {
          if (!isValidElement(child)) return child;
          const extra = revealed ? 'animate-fade-in-up' : 'opacity-0';
          return cloneElement(child, {
            key: child.key ?? i,
            className: `${child.props.className || ''} ${extra}`.trim(),
            style: {
              ...(child.props.style || {}),
              animationDelay: revealed
                ? `${baseDelay + i * childDelay}s`
                : undefined,
            },
          });
        })}
      </Tag>
    );
  }

  return (
    <Tag
      ref={ref}
      className={`${className} ${
        revealed ? 'animate-fade-in-up' : 'opacity-0'
      }`.trim()}
      style={{
        ...(style || {}),
        animationDelay: revealed ? `${baseDelay}s` : undefined,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
